import React, { useEffect, useMemo, useRef, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import join from 'lodash/join';

import Pills from 'components/Pills';
import Spinner from 'components/Spinner';
import { ViewContainer } from 'modules/Travel/components/Container';
import CategoryToggle from 'modules/Distribution/components/CategoryToggle';
import DownloadMarketPresentationButton from 'modules/Travel/components/downloadMarketPresentationButton';
import Map from 'modules/Travel/components/Map';
import TravelLineGraphCard from 'modules/Travel/components/TravelLineGraphCard';
import TravelTableCard from 'modules/Travel/components/TravelTable';
import TravelPosCard from 'modules/Travel/components/TravelPosCard';

import { Option, OptionWithId } from 'interface';
import { formatParam } from 'utils/formatParam';
import {
  getTravelPillOptionsForDefault,
  getTravelPillOptions,
  isSpiritOnly,
  showNCBars
} from 'utils/personalisation';
import { getOption, sortPosByBrand, sortPosByRef } from 'utils/pos';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { useTravelData, useTravelPOSRefs } from 'apiTyped/hooks/travel';
import { ProductType } from 'apiTyped/hooks/notebooks';
import { useProductType } from 'contexts/ProductTypeContext';

const Travel = () => {
  const { user } = useUserMe();
  const isBranded = useMemo(() => user.isBranded, [user]);
  const clientTags: string[] = useMemo(() => user.client.tags || [], [user]);
  const hasWineshopAccess = useMemo(() => user.client.displayWineshops, [user]);
  const isSpiritOnlyFlag = isSpiritOnly(clientTags);
  const hasSpirit = showNCBars(clientTags);
  const shouldShowCategoryToggle = hasSpirit && !isSpiritOnlyFlag;

  const { productType, setProductType } = useProductType();

  // Initialize productType immediately
  const initialProductType = useMemo(() => {
    return isSpiritOnlyFlag ? ProductType.Spirit : ProductType.Wine;
  }, [isSpiritOnlyFlag]);

  const [selectMarket, setSelectMarket] = useState<number | undefined>(
    undefined
  );
  const [selectUSCampaign, setSelectUSCampaign] = useState(false);
  const [wines, setWines] = useState<string[]>([]);

  const [activePillRestaurant, setActivePillRestaurant] = useState<
    string | null
  >(null);
  const [activePillCave, setActivePillCave] = useState<string | null>(null);

  const {
    isLoading: isLoadingMap,
    availableMarkets,
    selectedMarket,
    pos: pointOfSales,
    ranks,
    history,
    wineKpi,
    brandKpi,
    clientWines,
    clientBrands
  } = useTravelData({
    market_id: selectMarket,
    wines: !user.isBranded && wines.length ? wines : undefined,
    brands: user.isBranded && wines.length ? wines : undefined,
    productType: productType
  });

  const { isLoading: isLoadingRefsData, pos: posRefsData } = useTravelPOSRefs({
    market_id: selectMarket,
    wines: !user.isBranded && wines.length ? wines : undefined,
    brands: user.isBranded && wines.length ? wines : undefined,
    productType: productType
  });

  const hasUSCampaigns = useMemo(() => {
    // true if any of the campaigns are US-based
    if (!availableMarkets) return false;
    return availableMarkets.some(
      (market) => market.market_human_validation === false
    );
  }, [availableMarkets]);

  const isOnlyUS = useMemo(() => {
    // true if all of the campaigns are US-based
    if (!availableMarkets || availableMarkets.length === 0) return false;
    return availableMarkets.every(
      (market) => market.market_human_validation === false
    );
  }, [availableMarkets]);

  useEffect(() => {
    if (isOnlyUS) {
      setSelectUSCampaign(true);
    }
  }, [isOnlyUS]);

  const selectMarketName = useMemo(() => {
    if (selectMarket) {
      const marketOption = availableMarkets.find(
        (m) => m.market_id === selectMarket
      );
      return marketOption?.market_name;
    }
    return get(selectedMarket, 'market_name', undefined);
  }, [selectMarket, selectedMarket, availableMarkets]);

  const exportName = useMemo(() => {
    return `${selectMarketName ? selectMarketName + '-' : ''}${
      wines.length > 0 ? join(wines, '-') + '-' : ''
    }`;
  }, [selectMarketName, wines]);

  const kpis = useMemo<any[]>(() => {
    return isBranded ? brandKpi : wineKpi;
  }, [isBranded, wineKpi, brandKpi]);

  const wineOptions = useMemo(() => {
    if (isBranded) {
      return clientBrands.map((item) => ({
        label: item.marque_name,
        value: item.marque_name
      }));
    }
    return clientWines.map((item) => ({
      label: item.wine_name,
      value: item.wine_name
    }));
  }, [clientWines, clientBrands]);

  const marketOptions = useMemo(() => {
    return availableMarkets
      .filter((item) => item.market_human_validation === !selectUSCampaign)
      .map((item) => ({
        value: item.market_id,
        label: item.market_name,
        name: item.market_name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableMarkets, selectUSCampaign]);

  useEffect(() => {
    // if the selected campaign is in the correct market, nothing to do
    if (
      availableMarkets.length > 0 &&
      selectMarket &&
      availableMarkets.some(
        (m) =>
          m.market_id === selectMarket &&
          m.market_human_validation === !selectUSCampaign
      )
    ) {
      return;
    }

    // If we flip from US to non-US campaigns, reset the market selection
    // pick set first market that matches the current selectUSCampaign
    const firstMarket = availableMarkets.find((m) =>
      selectUSCampaign
        ? m.market_human_validation === false
        : m.market_human_validation === true
    );
    if (firstMarket) {
      setSelectMarket(firstMarket.market_id);
    }
  }, [selectUSCampaign, availableMarkets, selectMarket]);

  const hasResetWindowURL = useRef(false);
  const hasInitializedProductType = useRef(false);

  // Initialize productType based on client tags (only once)
  useEffect(() => {
    if (
      !hasInitializedProductType.current &&
      productType !== initialProductType
    ) {
      setProductType(initialProductType);
      hasInitializedProductType.current = true;
    }
  }, [initialProductType, productType, setProductType]);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Only process URL parameters if we have availableMarkets and if selectMarket is not already set
    if (
      availableMarkets.length > 0 &&
      selectMarket === undefined &&
      !hasResetWindowURL.current
    ) {
      if (window.location.search) {
        let marketIdFromURL = window.location.search.replace('?market_id=', '');
        marketIdFromURL = marketIdFromURL.replace(/%20/gi, ' ');
        // Find the corresponding market_id for the market name
        const marketOption = availableMarkets.find(
          (m) => m.market_id === parseInt(marketIdFromURL)
        );
        if (marketOption) {
          setSelectMarket(marketOption.market_id);
          setSelectUSCampaign(marketOption.market_human_validation == false);
          hasResetWindowURL.current = true;
          // Remove the market_id parameter from the URL
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
  }, [availableMarkets, selectMarket]);

  useEffect(() => {
    // for The most distributed wines in restaurants & wine shops
    if (clientTags && (!activePillCave || !activePillRestaurant)) {
      const tagValues = getTravelPillOptionsForDefault(clientTags);
      const indexFoundTag = tagValues.findIndex((tag) =>
        clientTags.includes(tag)
      );
      if (indexFoundTag >= 0) {
        setActivePillCave(tagValues[indexFoundTag]);
        setActivePillRestaurant(tagValues[indexFoundTag]);
      }
    }
  }, [clientTags, activePillCave, activePillRestaurant]);

  const getTopPos = (network: string) => {
    return pointOfSales
      .filter((item) => item.network_code === network)
      .sort((a, b) => (b.dn_total ?? 0) - (a.dn_total ?? 0))
      .map((item, index) => ({
        ...item,
        rank: index + 1
      }))
      .splice(0, 6);
  };

  const getDataRankingOn = () => {
    if (activePillRestaurant && activePillRestaurant !== 'all') {
      return ranks
        .filter(
          (item) =>
            item.wine_category === activePillRestaurant &&
            item.network_code === 'ON'
        )
        .map((item) => ({ ...item, rank: item.rank_wine_category }))
        .sort((a, b) => a.rank - b.rank);
    }
    return ranks.filter(
      (item) => item.network_code === 'ON' && item.rank <= 100
    );
  };

  const getDataRankingOff = () => {
    if (activePillCave && activePillCave !== 'all') {
      return ranks
        .filter(
          (item) =>
            item.wine_category === activePillCave && item.network_code === 'OFF'
        )
        .map((item) => ({ ...item, rank: item.rank_wine_category }))
        .sort((a, b) => a.rank - b.rank);
    }
    return ranks.filter(
      (item) => item.network_code === 'OFF' && item.rank <= 100
    );
  };

  const topPosOn = getTopPos('ON');
  const topPosOff = getTopPos('OFF');

  const dataRankingOn = getDataRankingOn();
  const dataRankingOff = getDataRankingOff();

  const exportOptions = useMemo(() => {
    const entities =
      wines.length > 0
        ? formatParam(JSON.stringify(wines))
        : formatParam(JSON.stringify(wineOptions.map((item) => item.label)));

    // Use selectMarket if available, otherwise fall back to selectedMarket.market_id
    const marketId =
      selectMarket || get(selectedMarket, 'market_id', undefined);

    const options = {
      market_id: marketId,
      wines: undefined,
      brands: undefined
    };

    if (isBranded) {
      options.brands = entities;
    } else {
      options.wines = entities;
    }

    return options;
  }, [selectMarket, selectedMarket, wines, wineOptions, isBranded]);

  return (
    <ViewContainer>
      <div className="travelMapContainer">
        {shouldShowCategoryToggle && (
          <CategoryToggle
            value={productType === ProductType.Wine ? 'wine' : 'spirit'}
            className="downloadMarketPresentation"
            onChange={(value: string) => {
              setProductType(
                value === 'wine' ? ProductType.Wine : ProductType.Spirit
              );
            }}
          />
        )}
        <DownloadMarketPresentationButton
          className="downloadMarketPresentation"
          offLink={get(
            selectedMarket,
            'off_campaign_market_presentation_pdf',
            null
          )}
          onLink={get(
            selectedMarket,
            'on_campaign_market_presentation_pdf',
            null
          )}
        />
        <Map
          market={selectedMarket}
          markers={isLoadingMap ? [] : pointOfSales}
          optionsMarket={marketOptions}
          posRefsData={isLoadingRefsData ? [] : posRefsData}
          onChangeMarket={({ value }: OptionWithId) => setSelectMarket(value)}
          hasUSCampaigns={hasUSCampaigns}
          isOnlyUS={isOnlyUS}
          selectUSCampaign={selectUSCampaign}
          onChangeUSCampaign={(value: boolean) => setSelectUSCampaign(value)}
          wines={wines.map((wine) => getOption(wine))}
          optionsWine={wineOptions}
          onChangeWines={(values: [Option]) =>
            setWines(values.map((e) => e.value))
          }
          isBranded={isBranded}
          hasWineshopAccess={hasWineshopAccess}
          productType={
            (productType || initialProductType) === ProductType.Spirit
              ? 'spirit'
              : 'wine'
          }
        />
      </div>
      <div className="travelContentContainer">
        <div className="firstTableContainer">
          <TravelTableCard
            name="table-pos"
            title={I18n.t('travel.titleTablePos')}
            data={pointOfSales
              .filter((item) => item.state === 'pos')
              .sort((a, b) =>
                isBranded
                  ? sortPosByBrand(
                      a.client_wines_by_brand,
                      b.client_wines_by_brand
                    )
                  : sortPosByRef(a.client_refs_by_wine, b.client_refs_by_wine)
              )}
            retractedSize={7}
            isBranded={isBranded}
            hasWineshopAccess={hasWineshopAccess}
            exportName={exportName}
            exportOptions={{
              path: '/travel/pos/xls?',
              options: exportOptions
            }}
            addReferencesColumn
          />
        </div>
        <TravelTableCard
          name="table-pos-lost"
          title={I18n.t('travel.titleTableLost')}
          data={pointOfSales
            .filter((item) => item.state === 'lost')
            .sort((a, b) =>
              isBranded
                ? sortPosByBrand(
                    a.rivals_wines_by_brand,
                    b.rivals_wines_by_brand
                  )
                : sortPosByRef(a.rival_wines, b.rival_wines)
            )}
          retractedSize={7}
          isBranded={isBranded}
          hasWineshopAccess={hasWineshopAccess}
          exportName={exportName}
          exportOptions={{
            path: '/travel/lost/xls?',
            options: exportOptions
          }}
        />
        <TravelTableCard
          name="table-pos-to-conquer"
          title={I18n.t('travel.titleTableConquer')}
          data={pointOfSales
            .filter((item) => item.state === 'todo')
            .sort((a, b) =>
              isBranded
                ? sortPosByBrand(
                    a.rivals_wines_by_brand,
                    b.rivals_wines_by_brand
                  )
                : sortPosByRef(a.rival_wines, b.rival_wines)
            )}
          retractedSize={7}
          isBranded={isBranded}
          hasWineshopAccess={hasWineshopAccess}
          exportName={exportName}
          exportOptions={{
            path: '/travel/conquest/xls?',
            options: exportOptions
          }}
        />
        <TravelTableCard
          name="table-pos-other"
          title={I18n.t('travel.titleTableOther')}
          data={pointOfSales.filter((item) => item.state === 'other')}
          retractedSize={7}
          isBranded={isBranded}
          hasWineshopAccess={hasWineshopAccess}
          exportName={exportName}
          exportOptions={{
            path: '/travel/other/xls?',
            options: exportOptions
          }}
        />
        <TravelLineGraphCard
          name="graph-distribution-restaurant"
          title={I18n.t('travel.titleDistributionRestaurant')}
          nameMarket={selectMarketName || ''}
          indicators={kpis.filter((item) => item.network_code === 'ON')}
          dataChart={history.filter((item) => item.network_code === 'ON')}
          tooltipLabel={I18n.t('restaurantsMaj')}
          client_brands={clientBrands}
          client_wines={clientWines}
          wineOptions={
            wines.length > 0 ? wines.map((e) => getOption(e)) : wineOptions
          }
          isBranded={isBranded}
          exportName={exportName}
          exportOptions={{
            path: '/travel/kpi/xls?',
            options: { ...exportOptions, network_code: 'ON' }
          }}
          isChampagne={clientTags.includes('Champagne')}
        />
        {get(selectedMarket, 'off_year') && (
          <TravelLineGraphCard
            name="graph-distribution-cave"
            title={I18n.t('travel.titleDistributionCave')}
            nameMarket={selectMarketName || ''}
            indicators={kpis.filter((item) => item.network_code === 'OFF')}
            dataChart={history.filter((item) => item.network_code === 'OFF')}
            tooltipLabel={I18n.t('wineShopsMaj')}
            client_brands={clientBrands}
            client_wines={clientWines}
            wineOptions={
              wines.length > 0 ? wines.map((e) => getOption(e)) : wineOptions
            }
            isBranded={isBranded}
            exportName={exportName}
            exportOptions={{
              path: '/travel/kpi/xls?',
              options: { ...exportOptions, network_code: 'OFF' }
            }}
            isChampagne={clientTags.includes('Champagne')}
          />
        )}
        <TravelPosCard
          name="table-restaurant-rank"
          title={
            <div className="travelPosTitleCard">
              {isBranded
                ? I18n.t('travel.titleRankBrand')
                : I18n.t('travel.titleRankRestaurant')}
              <Pills
                options={
                  getTravelPillOptions(clientTags).map((item: any) => ({
                    ...item,
                    active: item.value === activePillRestaurant
                  })) as any[]
                }
                onChange={({ value }: Option) => setActivePillRestaurant(value)}
                styles={{}}
              />
            </div>
          }
          type={I18n.t('restaurantsMaj')}
          dataTable={dataRankingOn}
          dataPos={topPosOn}
          retractedSize={7}
          exportName={exportName}
          exportOptions={{
            pathWine: '/travel/wine-rank/xls?',
            pathPos: '/travel/pos-rank/xls?',
            options: { ...exportOptions, network_code: 'ON' }
          }}
        />
        {get(selectedMarket, 'off_year') && (
          <TravelPosCard
            name="table-cave-rank"
            title={
              <div className="travelPosTitleCard">
                {isBranded
                  ? I18n.t('travel.titleRankBrand')
                  : I18n.t('travel.titleRankCave')}
                <Pills
                  options={
                    getTravelPillOptions(clientTags).map((item: any) => ({
                      ...item,
                      active: item.value === activePillCave
                    })) as any[]
                  }
                  onChange={({ value }: Option) => setActivePillCave(value)}
                  styles={{}}
                />
              </div>
            }
            type={I18n.t('wineShopsMaj')}
            exportName={exportName}
            dataTable={dataRankingOff}
            isBranded={isBranded}
            retractedSize={7}
            dataPos={topPosOff}
            exportOptions={{
              pathWine: '/travel/wine-rank/xls?',
              pathPos: '/travel/pos-rank/xls?',
              options: { ...exportOptions, network_code: 'OFF' }
            }}
          />
        )}
      </div>
      <Spinner loading={isLoadingMap} />
    </ViewContainer>
  );
};

export default Travel;
